/**
 * How every statistical surface presents itself — FR-STAT-17.
 *
 * One module, for the same reason `scoring-rules` is one module: the standings
 * screen, the club board, the profile tables and the shared card must agree
 * about which column matters and how a rank reads, and they will only keep
 * agreeing if there is a single place that says so. The legacy spreadsheet
 * failed by having the same rule copied into many cells until they drifted.
 *
 * Nothing here computes a statistic. Ranks and ordering arrive from the server
 * already correct — `StandingsService` and `LeaderboardService` both sort by
 * Match Points, then Win %, then Dominance, and give tied rows the same rank
 * (BR-AWARD-1). This module decides only how that is *shown*.
 */

/**
 * The column a view exists to communicate, emphasised and sorted on.
 *
 * Match Points for anything ranking players, because that is what the club's
 * scoring system produces and what Player of the Day is decided on. A dominance
 * view names Dominance instead; a participation view names Games.
 */
export type PrimaryColumn = "matchPoints" | "dominance" | "games";

/**
 * The marker that says "this column is what the table is sorted by, descending".
 *
 * Carried in addition to weight and the accent colour, never instead of them.
 * A shared card is read in a group chat on whatever screen someone has, and
 * roughly one man in twelve cannot rely on the orange to tell him anything —
 * so the emphasis has to survive being seen in greyscale.
 */
export const SORTED_DESC = "▼";

/** The class the emphasised column carries, in the header and every cell. */
export const PRIMARY_CLASS = "primary";

/**
 * A rank as it should read, given that tied players share a position.
 *
 * `=2` for a shared second place, plain `2` otherwise. The equals sign is the
 * convention the club will recognise from any league table, and it matters:
 * without it, two rows both reading `2` look like a bug rather than a tie, and
 * the row after them reading `4` looks like a second bug.
 */
export function rankText(rank: number, shared: boolean): string {
  return shared ? `=${String(rank)}` : String(rank);
}

/**
 * Ranking rows the server does not rank for us.
 *
 * Player standings and leaderboards arrive ranked; **pair standings do not** —
 * `PairStanding` has no `rank` field. Rather than number pairs by their array
 * index, which would give two level partnerships different positions, this
 * applies the same rule the server uses (BR-AWARD-1: Match Points, then Win %,
 * then Dominance, ties shared).
 *
 * Deliberately the same comparison, deliberately not the same code: this ranks
 * for display and cannot reach the database. If the rule ever changes, it
 * changes in `scoring-rules` and both callers follow — the comment is here so
 * that whoever changes it knows this exists.
 */
export interface Rankable {
  matchPoints: number;
  winPct: number | null;
  dominance: number | null;
}

export function ranked<T extends Rankable>(rows: readonly T[]): Array<T & { rank: number; shared: boolean }> {
  const sorted = [...rows].sort(
    (a, b) =>
      b.matchPoints - a.matchPoints ||
      (b.winPct ?? 0) - (a.winPct ?? 0) ||
      (b.dominance ?? 0) - (a.dominance ?? 0),
  );

  const level = (a: Rankable, b: Rankable): boolean =>
    a.matchPoints === b.matchPoints &&
    (a.winPct ?? 0) === (b.winPct ?? 0) &&
    (a.dominance ?? 0) === (b.dominance ?? 0);

  return sorted.map((row) => ({
    ...row,
    // The position of the *first* row with these figures, so a tie shares a
    // number and the row after the tie skips one.
    rank: sorted.findIndex((other) => level(row, other)) + 1,
    shared: sorted.filter((other) => level(row, other)).length > 1,
  }));
}

/**
 * ── the shared card's columns, and what gets dropped when they do not fit ────
 *
 * FR-STAT-16 requires a documented drop order rather than silent truncation, and
 * requires that the columns making `MP = Wins + Bonus − Penalties` checkable are
 * never among the dropped. That identity is the club's own arithmetic; a card
 * showing MP without its components invites exactly the "that can't be right"
 * argument this rebuild exists to end.
 *
 * `droppable` is ordered **least useful first**, so dropping proceeds down the
 * list until the row fits:
 *
 *   1. Dom%  — normalised dominance. Meaningful only when a session mixes 15s
 *              and 21s, and the raw figure beside it already says most of it.
 *   2. Win%  — derivable from G and W by anyone who wants it.
 *   3. Dom   — the last of the "how heavily" columns to go.
 *   4. PL    — points lost. Goes before PW because a card that has kept PW
 *              still shows how much someone scored.
 *
 * Everything else is fixed: G, W, Bon, Pen, MP and the name. Four droppable
 * columns is enough headroom for any name length the club will produce, and if
 * it ever is not, the honest fix is a wider card rather than a shorter identity.
 */
export interface CardColumn {
  key: "games" | "wins" | "winPct" | "pointsWon" | "pointsLost" | "dominance" | "normalisedDominance" | "bonus" | "penalties" | "matchPoints";
  label: string;
  /** Rendered width budget at the card's own scale. */
  width: number;
}

export const CARD_COLUMNS: readonly CardColumn[] = [
  { key: "games", label: "G", width: 62 },
  { key: "wins", label: "W", width: 62 },
  { key: "winPct", label: "Win%", width: 92 },
  { key: "pointsWon", label: "PW", width: 86 },
  { key: "pointsLost", label: "PL", width: 86 },
  { key: "dominance", label: "Dom", width: 88 },
  { key: "normalisedDominance", label: "Dom%", width: 92 },
  { key: "bonus", label: "Bon", width: 68 },
  { key: "penalties", label: "Pen", width: 68 },
  { key: "matchPoints", label: "MP", width: 78 },
];

/** In the order they are given up. See the note above. */
export const CARD_DROP_ORDER: ReadonlyArray<CardColumn["key"]> = [
  "normalisedDominance",
  "winPct",
  "dominance",
  "pointsLost",
];

/** Never dropped: the identity, plus the two columns everything else is read against. */
export const CARD_NEVER_DROP: ReadonlyArray<CardColumn["key"]> = [
  "games",
  "wins",
  "bonus",
  "penalties",
  "matchPoints",
];

/**
 * The widest set of columns that fits the space left after the name column.
 *
 * Returns them in display order, not drop order. Dropping stops as soon as the
 * remainder fits, so a card only loses what it has to.
 */
export function columnsThatFit(available: number): CardColumn[] {
  const kept = new Map(CARD_COLUMNS.map((c) => [c.key, c]));

  for (const key of CARD_DROP_ORDER) {
    const total = [...kept.values()].reduce((sum, c) => sum + c.width, 0);
    if (total <= available) break;
    if (CARD_NEVER_DROP.includes(key)) continue; // belt and braces; the lists are disjoint
    kept.delete(key);
  }

  return CARD_COLUMNS.filter((c) => kept.has(c.key));
}
